import { ComputedRef, computed, ref } from 'vue';
import { interpolateBetween } from '@/ts/util/color-util';
import { interpolateBetweenColors, isDarkColor, rgbComponentsToHex } from '@/ts/util/colors';
import { getWeightBetweenValues } from '@/ts/util/math';
import { previousCurrentPairIterator } from '@/ts/util/iterators';
import { Period } from '@/ts/composables/usePeriod';
import { BollingerBandWidthCalculator, Calculator } from '@/ts/map/calculationFunctions';

const INTERPOLATE_AMOUNT = 5;
export const HEADER_COLOR_SCHEME = interpolateBetween(['#000000', '#979797'], INTERPOLATE_AMOUNT);

const COLOR_SCHEMES_MAP = {
  'red-gray-green': ['#660007', '#b3000c', '#e6000f', '#ff4d58', '#8d99a5', '#5ad895', '#2cba6e', '#1d7c49', '#0f3e25'],
  'red-white-blue': ['#660007', '#b3000c', '#e6000f', '#ff4d58', '#b3b3b3', '#7a7be6', '#4142a6',  '#2b2d87', '#141661'],
  'red-black-green': interpolateBetween(['#e6000f', '#000000', '#2cba6e'], 3),
  'monochrome': interpolateBetween(['#1a1a1a', '#e8e8e8'], 7),
  'vanilla': interpolateBetween(['#911919', '#FFFFCC', '#14752E'], 3),
  'mint': interpolateBetween(['#660066', '#CCFFCC', '#006633'], 3),
  'lavender': interpolateBetween(['#b3000c', '#E6E6FA', '#7272c2'], 3),
  'blackberry': interpolateBetween(['#ff4d58', '#000000', '#7272c2'], 3),
  'cotton-candy': interpolateBetween(['#4054e6', '#bea2e8', '#e33bb3'], 3),
  'sunset': interpolateBetween(['#46066b', '#b84402', '#dbb704'], 3)
} as const;

type ColorSchemeKey = keyof typeof COLOR_SCHEMES_MAP;

function isValidColorScheme(value: string): value is ColorSchemeKey {
  return Object.keys(COLOR_SCHEMES_MAP).includes(value);
}

const DEFAULT_COLOR_SCHEME: ColorSchemeKey = 'red-gray-green';

function matchValueToColorRange(colorToNumberSpan: [string, number][], value: number) {
  const lowerBoundEntry = colorToNumberSpan[0];
  const upperBoundEntry = colorToNumberSpan[colorToNumberSpan.length - 1];

  for (const [currentEntry, nextEntry] of previousCurrentPairIterator(colorToNumberSpan)) {
    if (value === currentEntry[1]) {
      return {
        fromColorValuePair: currentEntry,
        toColorValuePair: currentEntry
      };
    }

    if (value > currentEntry[1] && value < nextEntry[1]) {
      return {
        fromColorValuePair: currentEntry,
        toColorValuePair: nextEntry
      };
    }
  }

  if (value <= lowerBoundEntry[1]) {
    return {
      fromColorValuePair: lowerBoundEntry,
      toColorValuePair: lowerBoundEntry
    };
  }

  if (value >= upperBoundEntry[1]) {
    return {
      fromColorValuePair: upperBoundEntry,
      toColorValuePair: upperBoundEntry
    };
  }

  return {
    fromColorValuePair: lowerBoundEntry,
    toColorValuePair: upperBoundEntry
  };
}

// Factory for creating instance-scoped color scheme state
export function createColorScheme(
  dependencies: {
    numericSteps: ComputedRef<number[]>;
    calculator: ComputedRef<Calculator>;
    period: ComputedRef<number>;
  },
  options?: { colorScheme?: ColorSchemeKey }
) {
  const colorSchemeKey = ref<ColorSchemeKey>(options?.colorScheme ?? DEFAULT_COLOR_SCHEME);

  const colorScheme = computed(() => {
    const colors = COLOR_SCHEMES_MAP[colorSchemeKey.value];
    const shouldOnlyShowNeutralToPositiveValues = dependencies.calculator.value instanceof BollingerBandWidthCalculator && dependencies.period.value === Period.Latest;
    if (shouldOnlyShowNeutralToPositiveValues) {
      const midPoint = Math.floor(colors.length / 2);
      return interpolateBetween(colors.slice(midPoint), 1);
    }
    return colors;
  });

  const colorToNumberSpan = computed<[string, number][]>(() => {
    return dependencies.numericSteps.value.map((value, index) => [colorScheme.value[index], value]);
  });

  function getParentColor(depth: number) {
    return {
      boxColor: HEADER_COLOR_SCHEME[Math.floor(depth)],
      fontColor: '#FFFFFF'
    };
  }

  function getLeafColor(value: number) {
    const { fromColorValuePair, toColorValuePair } = matchValueToColorRange(colorToNumberSpan.value, value);
    const [fromColor, fromValue] = fromColorValuePair;
    const [toColor, toValue] = toColorValuePair;
    const weight = getWeightBetweenValues(value, fromValue, toValue);
    const newColor = interpolateBetweenColors(fromColor, toColor, weight);
    const isDark = isDarkColor(newColor);
    const color = rgbComponentsToHex(newColor);

    return {
      boxColor: color,
      fontColor: isDark ? '#FFFFFF' : '#000000'
    };
  }

  return {
    colorScheme,
    colorSchemeKey,
    colorToNumberSpan,
    getParentColor,
    getLeafColor
  };
}

export type { ColorSchemeKey };

export {
  DEFAULT_COLOR_SCHEME,
  isValidColorScheme
};
